import {
  Directive,
  forwardRef,
  input,
  InputSignal,
  effect,
  inject
} from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';
import { NumberParserService } from '../../../services/number/number-parser.service';

import type {
  Provider,
  Type
} from '@angular/core';
import type {
  AbstractControl,
  ValidationErrors,
  Validator,
  ValidatorFn
} from '@angular/forms';

export const MIN_VALUE_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<MinValueValidator> => MinValueValidator),
  multi: true
};

/**
 * MinValueValidator Directive
 *
 * Allows validation of minimum values in a numeric input field
 *
 * @example
 * <input type="number" min="0"/>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[min][formControlName],[min][formControl],[min][ngModel]',
  providers: [MIN_VALUE_VALIDATOR]
})
export class MinValueValidator implements Validator {
  // underlying input signal (alias preserves attribute binding)
  readonly minSignal: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'min' });
  // override storage for programmatic assignments (tests, runtime setters)
  private _minOverride?: number;
  // expose numeric property for compatibility
  get min(): number | undefined { return this._minOverride ?? this.minSignal(); }
  set min(v: number | undefined) {
    this._minOverride = v;
    if (v !== undefined) {
      this.createValidator(v);
      if (this.validatorOnChange !== undefined) {
        this.validatorOnChange();
      }
    }
  }

  // neutral validator so validate() is safe before inputs initialize
  private validatorFn: ValidatorFn = () => null;
  private validatorOnChange?: () => void;
  private readonly numberParserService: NumberParserService = inject(NumberParserService);

  constructor() {
    effect(() => {
      const m = this.minSignal();
      if (m !== undefined) {
        this.createValidator(m);
        if (this.validatorOnChange !== undefined) {
          this.validatorOnChange();
        }
      }
    });
  }

  /**
   * @param minValue
   * @param numberParserService
   */
  static min(minValue: number, numberParserService: NumberParserService): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const actualValue: number = numberParserService.parse(String(control.value));
      return !isNaN(actualValue) && actualValue < minValue ? { min : { minValue , actualValue } } : null;
    };
  }

  /**
   * See Validator.validate
   *
   * @param abstractControl
   * @returns
   */
  validate(abstractControl: AbstractControl): ValidationErrors | null {
    return this.validatorFn(abstractControl);
  }

  /**
   * See Validator.registerOnValidatorChange
   *
   * @param validatorOnChange
   */
  registerOnValidatorChange(validatorOnChange: () => void): void {
    this.validatorOnChange = validatorOnChange;
  }

  validateValue(value: unknown): boolean {
    const actualValue: number = this.numberParserService.parse(String(value));
    if (isNaN(actualValue)) {
      return false;
    }
    const min = this.min;
    if (min === undefined) {
      // no min constraint -> value is considered valid
      return true;
    }
    return actualValue >= min;
  }

  private createValidator(minValue: number): void {
    this.validatorFn = MinValueValidator.min(minValue, this.numberParserService);
  }
}
